import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { MultiSelectPopover, type MultiSelectOption } from '@/components/ui/multi-select-popover';
import type { AgentApiCompaniesSummaryItem } from '@/types/serializers';
import useFetchSummaryCompanies from '../../api/useFetchSummaryCompanies';
import useFetchCompaniesByIds from '../../api/useFetchCompaniesByIds';
import { parseCompanyIds, buildCompanyIdsContent } from '../types/TagCondition';

interface Props {
  value: string;
  onChange: (content: string) => void;
}

/** 求人タグの企業選択条件用UI。検索追加・削除済み企業の可視化を担う */
export default function CompanyMultiSelectCondition({ value, onChange }: Props) {
  const selectedIds = useMemo(() => parseCompanyIds(value), [value]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchQuery]);

  const {
    data: searchData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchSummaryCompanies(debouncedQuery);
  const searchedCompanies = useMemo(
    () => searchData?.pages.flatMap((page) => page.records) ?? [],
    [searchData]
  );

  const {
    data: idCompanies,
    isFetched: isIdsFetched,
    isError: isIdsError,
  } = useFetchCompaniesByIds(selectedIds);

  const [nameCache, setNameCache] = useState<Record<number, string>>({});
  const registerNames = (companies: AgentApiCompaniesSummaryItem[]) => {
    if (companies.length === 0) return;
    setNameCache((prev) => {
      const next = { ...prev };
      companies.forEach((company) => {
        next[company.id] = company.name;
      });
      return next;
    });
  };
  useEffect(() => registerNames(idCompanies ?? []), [idCompanies]);
  useEffect(() => registerNames(searchedCompanies), [searchedCompanies]);

  const options: MultiSelectOption[] = useMemo(() => {
    const map = new Map<number, MultiSelectOption>();
    searchedCompanies.forEach((company) =>
      map.set(company.id, { id: company.id, name: company.name })
    );
    selectedIds.forEach((id) => {
      if (map.has(id)) return;
      const name =
        nameCache[id] ??
        (isIdsError
          ? `企業名を取得できませんでした（ID: ${id}）`
          : isIdsFetched
            ? `削除済み企業（ID: ${id}）`
            : '読み込み中…');
      map.set(id, { id, name });
    });
    return Array.from(map.values());
  }, [searchedCompanies, selectedIds, nameCache, isIdsFetched, isIdsError]);

  const handleSelectChange = (nextValues: (number | string)[]) => {
    onChange(buildCompanyIdsContent(nextValues.map((v) => Number(v))));
  };

  return (
    <div className="flex flex-col gap-2">
      <MultiSelectPopover
        options={options}
        value={selectedIds}
        onChange={handleSelectChange}
        placeholder="企業を検索して選択"
        searchable
        query={searchQuery}
        onQueryChange={setSearchQuery}
        columns={1}
        renderSelectedSummary={(count) => `${count}社選択中`}
        footer={
          hasNextPage && (
            <Button
              type="button"
              color="tertiary"
              size="sm"
              className="w-full"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? '読み込み中...' : 'もっと読み込む'}
            </Button>
          )
        }
      />
    </div>
  );
}
